<script setup lang="ts">
import CommonBreadcrumb from '~/components/common/Breadcrumb.vue'
import { getRegulatorMemberList } from '~/api/regulator-member'
import { getRegulatorCodeByEnShortName } from '~/api/regulator-list'

definePageMeta({
  name: 'disclosures-members',
  layout: 'default',
})

const route = useRoute()
const { t, localePath } = useAppI18n()
const { getBreadcrumb } = useLsBreadcrumb()
const breadcrumbObj = ref<any>({})

/** 当前监管机构 slug，兼容 Nuxt 动态路由数组入参。 */
const regulatorSlug = computed(() => normalizeRouteValue(route.params.slug))

/** 通过当前路由 slug 解析监管机构 code。 */
const { data: regulatorCodeData } = await useAsyncData(
  () => `regulator-member-code-${regulatorSlug.value}`,
  () => getRegulatorCodeByEnShortName(regulatorSlug.value),
  {
    watch: [regulatorSlug],
  },
)
const regulatorCode = computed(() => regulatorCodeData.value || '')

/** 当前监管机构的展示名称。 */
const regulatorDisplayName = computed(() => regulatorSlug.value)

/** 获取监管会员列表数据。 */
const { data: memberData } = await useAsyncData(
  () => `regulator-member-${regulatorCode.value}`,
  () => {
    if (!regulatorCode.value) {
      return null
    }

    return getRegulatorMemberList({ regulatorCode: regulatorCode.value, size: 50 })
  },
  {
    watch: [regulatorCode],
  },
)

/** 会员接口返回的交易所列表和总数。 */
const memberRows = computed(() => memberData.value?.memberList || [])
const memberCount = computed(() => memberData.value?.memberCount || 0)

/** 页面面包屑，复用项目公共组件。 */
const breadcrumbItems = computed(() =>
  [{ label: t('82520'), route: '/' }, breadcrumbObj.value, { label: t('84625') }].filter(Boolean),
)

useSeoMeta({
  title: () => t('84872', [breadcrumbObj.value?.label]),
  ogTitle: () => t('84872', [breadcrumbObj.value?.label]),
  description: () => t('84878', [breadcrumbObj.value?.label]),
  ogDescription: () => t('84878', [breadcrumbObj.value?.label]),
})
/**
 * 标准化动态路由参数。
 * @param value 原始路由值
 * @returns 可用于展示的 slug
 */
function normalizeRouteValue(value: unknown) {
  if (Array.isArray(value)) {
    return String(value[0] || '').trim()
  }

  return String(value || '').trim()
}

/**
 * 获取交易所详情页的本地化链接。
 * @param exchangeSlug 交易所别名
 * @returns 详情页路径
 */
function getExchangeDetailLink(exchangeSlug?: string) {
  return localePath({
    name: 'exchange-detail',
    params: { slug: exchangeSlug || '' },
  })
}

const { overflowMap, checkOverflow } = useTextOverflow()
const { setBreadcrumb } = useLsBreadcrumb()

/** 缓存交易所详情页的列表来源面包屑。 */
const setExchangeDetailBreadcrumb = (broker: any) => {
  if (!broker.alias) return
  setBreadcrumb(broker.alias, {
    label: broker.brokerShortName,
    route: route.fullPath,
  })
}

onMounted(() => {
  breadcrumbObj.value = getBreadcrumb('disclosures-members')
})
</script>

<template>
  <main class="min-h-screen bg-white pt-[70px] pb-[80px]">
    <section class="mx-auto w-[1200px] max-[1248px]:mx-[24px] max-[1248px]:w-auto">
      <!-- 公共头部由 default layout 提供；页面从面包屑开始对齐 Figma 内容区。 -->
      <CommonBreadcrumb :items="breadcrumbItems" />

      <h1 class="m-[0] mt-[12px] text-[24px] leading-[32px] font-[600] text-[#292a2e]">
        {{ t('84625') }}&nbsp; {{ memberCount }}
      </h1>

      <div class="mt-[24px] overflow-x-auto">
        <div class="min-w-[1000px]">
          <!-- 表头与列表使用一致列宽，匹配黑名单页面的五列数据结构。 -->
          <div
            class="grid h-[44px] grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_110px_110px] items-center border-b border-[#112d73]"
          >
            <div class="px-[12px] text-[13px] leading-[20px] font-[400] text-[#7d818a]">{{ t('80337') }}</div>
            <div class="px-[12px] text-[13px] leading-[20px] font-[400] text-[#7d818a]">{{ t('84340') }}</div>
            <div class="px-[12px] text-[13px] leading-[20px] font-[400] text-[#7d818a]">{{ t('84341') }}</div>
            <div class="px-[12px] text-[13px] leading-[20px] font-[400] text-[#7d818a]">{{ t('82495') }}</div>
            <div class="px-[12px] text-center text-[13px] leading-[20px] font-[400] text-[#7d818a]">
              {{ t('84343') }}
            </div>
          </div>

          <div v-if="memberRows.length">
            <NuxtLink
              v-for="member in memberRows"
              :key="member.brokerCode"
              :to="getExchangeDetailLink(member.alias)"
              target="_blank"
              class="grid h-[80px] cursor-pointer grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_110px_110px] items-center border-b-[0.5px] border-[rgba(11,18,40,0.14)] text-inherit no-underline transition-colors duration-200 hover:bg-[#f8f8f8]"
              @click="setExchangeDetailBreadcrumb(member)"
            >
              <div class="flex min-w-0 items-center gap-[12px] px-[12px]">
                <div
                  class="flex size-[44px] shrink-0 items-center justify-center overflow-hidden rounded-[4px] bg-[#f3f6ff]"
                >
                  <CommonImage
                    class="size-full object-cover"
                    :src="member.brokerIcon"
                    :alt="member.brokerShortName"
                    width="44"
                    height="44"
                    object-fit="cover"
                  />
                </div>

                <div class="flex min-w-0 flex-col gap-[2px]">
                  <div class="flex h-[24px] min-w-0 items-center gap-[8px]">
                    <el-tooltip
                      :content="member.brokerShortName"
                      placement="top"
                      popper-class="regulator-member-tooltip"
                      :show-after="0"
                      :offset="6"
                      :disabled="!overflowMap[member.brokerCode || '']"
                    >
                      <p
                        class="m-[0] line-clamp-2 max-w-full min-w-0 text-[16px] leading-[24px] font-[500] break-words text-[#292a2e]"
                        @mouseenter="checkOverflow($event, member.brokerCode || '')"
                      >
                        {{ member.brokerShortName || '--' }}
                      </p>
                    </el-tooltip>
                    <CommonImage
                      v-if="member.ultimateUltraIco"
                      :src="member.ultimateUltraIco"
                      class="ultra-icon"
                      alt=""
                      loading="eager"
                    />
                  </div>
                  <div class="flex items-center gap-[8px] text-[12px] leading-[18px]">
                    <span v-if="member.tags && member.tags.length > 0" :style="{ color: member.tags[0]?.textColor }">{{
                      member.tags[0]?.text
                    }}</span>
                    <span
                      v-if="member.tags && member.tags.length > 1"
                      class="h-[14px] w-px bg-[rgba(11,18,40,0.14)]"
                    ></span>
                    <span v-if="member.tags && member.tags.length > 1" :style="{ color: member.tags[1]?.textColor }">{{
                      member.tags[1]?.text
                    }}</span>
                  </div>
                </div>
              </div>

              <div class="min-w-0 px-[12px]">
                <el-tooltip
                  v-if="member.businessTypes"
                  :content="member.businessTypes"
                  placement="top"
                  popper-class="regulator-member-tooltip"
                  :show-after="0"
                  :offset="6"
                  :disabled="!overflowMap[member.brokerCode + '_biz']"
                >
                  <p
                    class="m-[0] inline-block max-w-full truncate text-[14px] leading-[22px] font-[500] text-[#292a2e]"
                    @mouseenter="checkOverflow($event, member.brokerCode + '_biz')"
                  >
                    {{ member.businessTypes || '--' }}
                  </p>
                </el-tooltip>
                <p v-else class="m-[0] truncate text-[14px] leading-[22px] font-[500] text-[#292a2e]">--</p>
              </div>

              <div class="min-w-0 px-[12px]">
                <p class="m-[0] truncate text-[14px] leading-[22px] font-[500] text-[#292a2e]">
                  {{ member.countryName || '--' }}
                </p>
              </div>

              <div class="flex items-center gap-[8px] px-[12px] text-[18px] leading-[26px] font-[500] text-[#292a2e]">
                <CommonImage
                  v-if="member.score"
                  src="https://resources1.interface003.com/web30/2026/06/27/c39ab257-18e0-423e-8579-d81791e03605.png"
                  class="font-wiki-Medium h-[20px] w-[20px] text-[#292a2e]"
                />
                <span class="font-wiki-Medium font-wiki-Bold">{{ member.score || '--' }}</span>
              </div>

              <div class="flex justify-center px-[12px]">
                <span
                  class="inline-flex h-[32px] w-[86px] items-center justify-center rounded-[20px] bg-[#112d73] text-[12px] leading-[18px] font-[500] text-white transition-colors duration-200 hover:bg-[#0d245c]"
                >
                  {{ t('84342') }}
                </span>
              </div>
            </NuxtLink>
          </div>

          <div v-else class="flex min-h-[200px] items-center justify-center text-[14px] text-[#7d818a]">
            {{ t('83714') }}
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<style lang="less">
.ultra-row {
  background: linear-gradient(180deg, #ffffff 0%, #f8f5ff 100%);
}

.ultra-icon {
  width: 25px;
  height: 15px;
  flex: 0 0 25px;
}

.regulator-member-tooltip.el-popper {
  max-width: 560px;
  padding: 7px 12px !important;
  border: none !important;
  border-radius: 4px !important;
  background: #ffffff !important;
  box-shadow:
    0 0 1px 0 rgba(0, 0, 0, 0.2),
    0 8px 20px 0 rgba(0, 0, 0, 0.12) !important;
  color: #292a2e !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  line-height: 22px !important;
  text-align: center;
  white-space: normal !important;
  word-break: break-all !important;

  .el-popper__arrow::before {
    border: none !important;
    background: #ffffff !important;
  }
}
</style>
